export interface VoteIntentViewNamespace {
  initModal: (modal: HTMLElement | null) => VoteIntentModal | null;
}

export interface VoteIntentModal {
  readonly modal: HTMLElement;
  readonly title: HTMLElement | null;
  readonly base: VoteIntentBaseElements;
  readonly success: VoteIntentSuccessElements;
  getMode: () => VoteIntentMode;
  setMode: (mode: VoteIntentMode) => void;
  reset: () => void;
  focusEmail: () => void;
  setLoginLinkEnabled: (enabled: boolean) => void;
  setPasswordOptionVisible: (visible: boolean) => void;
  setBaseStatus: (message: string) => void;
  setSuccessStatus: (message: string) => void;
  setSuccessEmail: (email: string) => void;
}

export interface VoteIntentBaseElements {
  readonly container: HTMLElement;
  readonly emailInput: HTMLInputElement | null;
  readonly sendButton: HTMLButtonElement | null;
  readonly turnstileContainer: HTMLElement | null;
  readonly loginLink: HTMLAnchorElement | null;
  readonly passwordOption: HTMLElement | null;
  readonly status: HTMLElement | null;
  readonly charityInput: HTMLInputElement | null;
  readonly periodInput: HTMLInputElement | null;
}

export interface VoteIntentSuccessElements {
  readonly container: HTMLElement;
  readonly emailLabel: HTMLElement | null;
  readonly resendButton: HTMLButtonElement | null;
  readonly loginLink: HTMLAnchorElement | null;
  readonly status: HTMLElement | null;
}

export type VoteIntentMode = 'base' | 'success';

const HIDDEN_CLASS = 'u-hidden';

function toggle(element: HTMLElement | null, show: boolean): void {
  if (!element) {
    return;
  }

  element.hidden = !show;
  if (!show) {
    element.classList.add(HIDDEN_CLASS);
    return;
  }

  element.classList.remove(HIDDEN_CLASS);
}

function textContent(node: Element | null, value: string): void {
  if (!node) {
    return;
  }

  node.textContent = value;
}

function setLinkEnabled(link: HTMLAnchorElement | null, enabled: boolean): void {
  if (!link) {
    return;
  }

  if (enabled) {
    link.classList.remove('u-hidden');
    link.removeAttribute('aria-disabled');
    link.removeAttribute('tabindex');
    return;
  }

  link.classList.add('u-hidden');
  link.setAttribute('aria-disabled', 'true');
  link.setAttribute('tabindex', '-1');
}

function setPasswordOptionVisible(node: HTMLElement | null, visible: boolean): void {
  if (!node) {
    return;
  }

  if (visible) {
    node.classList.remove('u-hidden');
    node.removeAttribute('aria-hidden');
    return;
  }

  node.classList.add('u-hidden');
  node.setAttribute('aria-hidden', 'true');
}

export function initModal(modal: HTMLElement | null): VoteIntentModal | null {
  if (!modal) {
    return null;
  }

  const contentRoot = modal.querySelector<HTMLElement>('.c-modal__content');
  if (!contentRoot) {
    return null;
  }

  const titleEl = modal.querySelector<HTMLElement>('#pif-vote-intent-title');

  const baseContainer = document.createElement('div');
  baseContainer.id = 'pif-vote-intent-base';
  while (contentRoot.firstChild) {
    baseContainer.appendChild(contentRoot.firstChild);
  }
  contentRoot.appendChild(baseContainer);

  const successContainer = document.createElement('div');
  successContainer.id = 'pif-vote-intent-success';
  successContainer.className = 'pif-vote-intent__success u-hidden';
  successContainer.hidden = true;
  successContainer.innerHTML = `
      <div class="pif-vote-intent__context" aria-live="polite">
        <div>We&#8217;ve sent a one-time sign-in link to <strong id="pif-vote-intent-success-email"></strong>.</div>
        <div>If it doesn&#8217;t arrive, check spam or try resending below.</div>
      </div>
      <div class="pif-actions">
        <button class="c-btn c-btn--primary" id="pif-vote-intent-success-resend">Resend link</button>
        <div class="c-help" role="status" aria-live="polite" id="pif-vote-intent-success-status"></div>
      </div>
    `;
  contentRoot.appendChild(successContainer);

  const base = {
    container: baseContainer,
    emailInput: baseContainer.querySelector<HTMLInputElement>('#pif-vote-intent-email'),
    sendButton: baseContainer.querySelector<HTMLButtonElement>('#pif-vote-intent-send-link'),
    turnstileContainer: baseContainer.querySelector<HTMLElement>('.pifster-turnstile'),
    loginLink: baseContainer.querySelector<HTMLAnchorElement>('#pif-vote-intent-login-link'),
    passwordOption: baseContainer.querySelector<HTMLElement>('#pif-vote-intent-password-option'),
    status: baseContainer.querySelector<HTMLElement>('#pif-vote-intent-status'),
    charityInput: modal.querySelector<HTMLInputElement>('#pif-vote-intent-charity-id'),
    periodInput: modal.querySelector<HTMLInputElement>('#pif-vote-intent-period-id'),
  };

  if (base.emailInput) {
    try {
      base.emailInput.setAttribute('type', 'email');
    } catch {
      // no-op
    }

    try {
      base.emailInput.setAttribute('autocomplete', 'email');
    } catch {
      // no-op
    }

    try {
      base.emailInput.setAttribute('inputmode', 'email');
    } catch {
      // no-op
    }

    if (base.status && base.status.id) {
      try {
        base.emailInput.setAttribute('aria-describedby', base.status.id);
      } catch {
        // no-op
      }
    }

    try {
      base.emailInput.setAttribute('aria-label', 'Email address');
    } catch {
      // no-op
    }

    const emailId = base.emailInput.id || 'pif-vote-intent-email';
    const label = base.container.querySelector<HTMLLabelElement>(`label[for="${emailId}"]`);
    if (label) {
      label.classList.add('sr-only');
    }
  }

  const success = {
    container: successContainer,
    emailLabel: successContainer.querySelector<HTMLElement>('#pif-vote-intent-success-email'),
    resendButton: successContainer.querySelector<HTMLButtonElement>('#pif-vote-intent-success-resend'),
    loginLink: successContainer.querySelector<HTMLAnchorElement>('#pif-vote-intent-success-login'),
    status: successContainer.querySelector<HTMLElement>('#pif-vote-intent-success-status'),
  };

  let mode: VoteIntentMode = 'base';

  function setTitleForMode(current: VoteIntentMode): void {
    if (!titleEl) {
      return;
    }

    titleEl.textContent = current === 'success' ? 'Check your email' : 'Sign in to vote';
  }

  function setMode(nextMode: VoteIntentMode): void {
    mode = nextMode;
    toggle(base.container, nextMode === 'base');
    toggle(success.container, nextMode === 'success');
    setTitleForMode(nextMode);

    if (nextMode !== 'base') {
      textContent(base.status, '');
    }

    if (nextMode !== 'success') {
      textContent(success.status, '');
    }
  }

  function reset(): void {
    setMode('base');
    textContent(base.status, '');
    textContent(success.status, '');
  }

  return {
    modal,
    title: titleEl,
    base,
    success,
    getMode: () => mode,
    setMode,
    reset,
    focusEmail: () => {
      if (base.emailInput) {
        base.emailInput.focus();
      }
    },
    setLoginLinkEnabled: (enabled: boolean) => {
      setLinkEnabled(base.loginLink, enabled);
    },
    setPasswordOptionVisible: (visible: boolean) => {
      setPasswordOptionVisible(base.passwordOption, visible);
    },
    setBaseStatus: (message: string) => {
      textContent(base.status, message);
    },
    setSuccessStatus: (message: string) => {
      textContent(success.status, message);
    },
    setSuccessEmail: (email: string) => {
      textContent(success.emailLabel, email);
    },
  };
}

export {};
